import { DashboardAndAnalysisFiltersPreview } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview';
import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import {
  useGetQuantifyCompareQuery,
  useGetQuantifyWhereClause,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { alpha } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

interface CompareFilterDefinitionInfoProps {
  shouldHideTooltips?: boolean;
  compareField: 'A' | 'B';
  shouldIncludeDashboardFilters?: boolean;
  shouldShowDashboardFiltersSeparately?: boolean;
}

export const CompareFilterDefinitionInfo = ({
  shouldHideTooltips,
  compareField,
  shouldIncludeDashboardFilters = true,
  shouldShowDashboardFiltersSeparately = true,
}: CompareFilterDefinitionInfoProps) => {
  const primaryCompareQuery = useGetQuantifyCompareQuery({
    shouldIncludeDashboardFilters: false,
  });
  const primaryWhereClauseA = useGetQuantifyWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const compareQuery = useGetQuantifyCompareQuery();
  const whereClauseA = useGetQuantifyWhereClause();

  const dashboardWhereClause = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters?.whereClause ?? EmptyWhereClause,
  );

  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );

  if (!primaryCompareQuery?.whereClause) {
    return null;
  }

  const primaryCompareWhereClause =
    compareField === 'A'
      ? primaryWhereClauseA
      : primaryCompareQuery?.whereClause ?? EmptyWhereClause;

  const compareWhereClause =
    compareField === 'A'
      ? whereClauseA
      : compareQuery?.whereClause ?? EmptyWhereClause;

  const compareFilterName =
    compareField === 'A' ? compareFilterAName : compareFilterBName;

  if (
    isEmptyWhereClause(dashboardWhereClause) ||
    !shouldIncludeDashboardFilters
  ) {
    return isEmptyWhereClause(primaryCompareWhereClause) ? (
      <>No {compareFilterName} filter applied</>
    ) : (
      <WhereClausePreview
        shouldHideTooltips={shouldHideTooltips}
        whereClause={primaryCompareWhereClause}
        sxProps={{
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.8),
        }}
      />
    );
  }

  if (!shouldShowDashboardFiltersSeparately) {
    return isEmptyWhereClause(compareWhereClause) ? (
      <>No {compareFilterName} filter applied</>
    ) : (
      <WhereClausePreview
        shouldHideTooltips={shouldHideTooltips}
        whereClause={compareWhereClause}
        sxProps={{
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.8),
        }}
      />
    );
  }

  return (
    <DashboardAndAnalysisFiltersPreview
      dashboardWhereClause={dashboardWhereClause}
      analysisWhereClause={primaryCompareWhereClause}
      shouldHideTooltips={shouldHideTooltips}
      analysisUseCase={AnalysisUseCase.Default}
    />
  );
};
